<script setup>
import { Head } from '@inertiajs/vue3'
import Button from '@/Components/Button.vue'
import { ref, onMounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const headerTitleRef = ref(null)
const headerSubtitleRef = ref(null)
const heroRef = ref(null)
const heroGridRef = ref(null)
const contentContainerRef = ref(null) // New ref for the content container

onMounted(() => {
    // Fade out header grid on scroll
    gsap.to(heroGridRef.value, {
        opacity: 0, 
        y: -50,
        scrollTrigger: {
            trigger: heroRef.value,
            start: 'top top',
            end: '+=300',
            scrub: true
        }
    });

    // Keep only the simple header animations
    gsap.fromTo(headerTitleRef.value, 
        { y: 60, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out' }
    );
    
    gsap.fromTo(headerSubtitleRef.value, 
        { y: 30, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1, delay: 0.4, ease: 'power2.out' }
    );
    
    // All other animations removed

    // Content container animation - similar to header animations
    gsap.fromTo(contentContainerRef.value, 
        { y: 40, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1.2, delay: 0.8, ease: 'power2.out' }
    );
})
</script>

<template>    
    <main>
        <div class="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:54px_54px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

        <div class="wide-container pt-32 md:pt-48 relative z-10">
            <div class="mx-auto max-w-3xl text-center">
                <h1 ref="headerTitleRef" class="text-pretty text-4xl font-semibold text-zinc-900 sm:text-5xl lg:text-6xl max-w-4xl tracking-tight leading-[120%]">Privacy & Cookies</h1>
                <p ref="headerSubtitleRef" class="text-balance text-xl text-zinc-600 max-w-2xl mx-auto mt-6">Hoe wij omgaan met uw persoonlijke gegevens en de bescherming van uw privacy.</p>
            </div>
        </div>
        
        <div class="wide-container">
            <div ref="contentContainerRef" class="max-w-screen-lg mx-auto text-zinc-600 space-y-16 -mb-16 sm:-mb-20 md:-mb-28">
                <!-- Section: Introduction -->
                <section ref="sectionRefs[0]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Inleiding</h2>
                    <p>Bij <span class="text-nowrap">c-o-d-e-s</span> vinden we je privacy belangrijk. Deze privacyverklaring legt uit welke persoonlijke gegevens we verzamelen en hoe we deze gebruiken wanneer je onze website bezoekt of gebruik maakt van onze diensten.</p>
                    <p>We voldoen aan de Algemene Verordening Gegevensbescherming (AVG) en andere toepasselijke privacywetgeving.</p>
                </section>
                
                <!-- Section: Information Collection -->
                <section ref="sectionRefs[1]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Gegevensverzameling</h2>
                    <p>We verzamelen de volgende informatie:</p>
                    <ul>
                        <li>Contactgegevens die u ons verstrekt: zoals naam, e-mailadres, telefoonnummer en bedrijfsinformatie.</li>
                        <li>Informatie van formulieren die u invult op onze website.</li>
                        <li>Technische gegevens: zoals IP-adres, browsertype, apparaattype en besturingssysteem.</li>
                        <li>Gebruiksgegevens: zoals hoe u onze website gebruikt, welke pagina's u bezoekt en hoe u door de site navigeert.</li>
                    </ul>
                </section>
                
                <!-- Section: Use of Information -->
                <section ref="sectionRefs[2]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Gebruik van informatie</h2>
                    <p>Wij gebruiken uw gegevens voor:</p>
                    <ul>
                        <li>Het leveren van onze diensten en producten</li>
                        <li>Het beantwoorden van uw vragen of verzoeken</li>
                        <li>Het verbeteren van onze website en diensten</li>
                        <li>Het verzenden van relevante informatie zoals nieuwsbrieven (alleen met uw toestemming)</li>
                        <li>Het voldoen aan wettelijke verplichtingen</li>
                    </ul>
                </section>
                
                <!-- Section: Cookies -->
                <section ref="sectionRefs[3]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Cookies</h2>
                    <p>Onze website gebruikt cookies om uw ervaring te verbeteren. Dit zijn kleine tekstbestanden die op uw apparaat worden opgeslagen. We gebruiken de volgende typen cookies:</p>
                    <ul>
                        <li><strong>Noodzakelijke cookies:</strong> vereist voor het functioneren van de website</li>
                        <li><strong>Analytische cookies:</strong> om te begrijpen hoe bezoekers onze website gebruiken</li>
                        <li><strong>Functionele cookies:</strong> om u te herkennen wanneer u terugkeert naar onze website</li>
                        <li><strong>Targeting cookies:</strong> om advertenties relevant te maken voor uw interesses</li>
                    </ul>
                    <p>Je kunt je browserinstellingen aanpassen om cookies te weigeren, maar dit kan je ervaring op onze website beïnvloeden.</p>
                </section>
                
                <!-- Section: Data Sharing -->
                <section ref="sectionRefs[4]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Delen van gegevens</h2>
                    <p>Wij delen uw persoonlijke gegevens alleen in de volgende gevallen:</p>
                    <ul>
                        <li>Met dienstverleners die ons helpen onze website te beheren en diensten te leveren</li>
                        <li>Wanneer we wettelijk verplicht zijn om gegevens te delen</li>
                        <li>Met uw toestemming</li>
                    </ul>
                    <p>We verkopen uw gegevens nooit aan derden.</p>
                </section>
                
                <!-- Section: Your Rights -->
                <section ref="sectionRefs[5]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Je rechten</h2>
                    <p>Onder de AVG heb je de volgende rechten:</p>
                    <ul>
                        <li>Recht op inzage in je persoonlijke gegevens</li>
                        <li>Recht op correctie van onjuiste gegevens</li>
                        <li>Recht op verwijdering van je gegevens</li>
                        <li>Recht op beperking van verwerking</li>
                        <li>Recht op gegevensoverdraagbaarheid</li>
                        <li>Recht op bezwaar tegen verwerking</li>
                    </ul>
                    <p>Om een van deze rechten uit te oefenen, kun je contact met ons opnemen via de contactgegevens onderaan deze pagina.</p>
                </section>
                
                <!-- Section: Contact -->
                <section ref="sectionRefs[6]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Contact</h2>
                    <p>Als u vragen heeft over deze privacyverklaring of hoe wij met uw gegevens omgaan, neem dan contact met ons op:</p>
                    <p>
                        <span class="text-nowrap">c-o-d-e-s</span> software development<br>
                        Brugstraat 11<br>
                        7607 XJ Almelo<br>
                        Nederland<br>
                        E-mail: privacy@codes.nl
                    </p>
                </section>
                
                <!-- Section: Updates -->
                <section ref="sectionRefs[7]" class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">Updates</h2>
                    <p>We kunnen deze privacyverklaring van tijd tot tijd bijwerken om veranderingen in onze praktijken weer te geven of om andere operationele, wettelijke of regelgevende redenen.</p>
                    <p>Laatste update: {{ new Date().toLocaleDateString('nl-NL', {day: 'numeric', month: 'long', year: 'numeric'}) }}</p>
                </section>
                
                <div class="mt-12 flex justify-center">
                    <Button href="/" variant="white" icon="chevron-right" class="group text-base sm:text-sm">Terug naar home</Button>
                </div>
            </div>
        </div>
    </main>
</template>
